import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useMatch } from '../../contexts/MatchContext';
import * as api from '../../api';
import { getScoresWithPoints, pad, getFlagSrc } from '../../utils/scoring';

export default function CompareShooters() {
  const { t } = useTranslation();
  const { match } = useMatch();
  const [shooters, setShooters] = useState(null);
  const [scores, setScores] = useState(null);
  const [stages, setStages] = useState(null);
  const [shooter1, setShooter1] = useState(null);
  const [shooter2, setShooter2] = useState(null);

  useEffect(() => {
    if (!match) return;
    window.scrollTo(0, 0);

    Promise.all([
      api.getShooters(match.id),
      api.getScores(match.id),
      match.stages?.length > 0 ? api.getStages(match.stages.join(',')) : Promise.resolve({ data: [] }),
    ]).then(([shootersRes, scoresRes, stagesRes]) => {
      setShooters((shootersRes.data || []).filter((s) => !s.cancelledParticipation));
      setScores(scoresRes.data?.scores || scoresRes.data || []);
      setStages(stagesRes.data || []);
    });
  }, [match?.id]);

  const { scoresByStage, scoresOverall } = useMemo(() => {
    if (!scores || !shooters || !stages) return {};
    return getScoresWithPoints({
      scores, shooters, stages, combined: true, mode: match.scoreMode,
    });
  }, [scores, shooters, stages]);

  if (!shooters || !scores || !stages) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const getShooterLabel = (shooter) => {
    if (!shooter) return '';
    const parts = (shooter.publicId || '').split('|');
    const displayId = parts[parts.length - 1];
    return `${displayId} - ${shooter.name} (${shooter.club || ''})`;
  };

  const getShooterScoreByStage = (shooterId) => {
    if (!scoresByStage) return [];
    return scoresByStage.map((stageScores) =>
      stageScores.find((s) => s.shooterId === shooterId)
    );
  };

  const getShooterOverall = (shooterId) => {
    if (!scoresOverall) return null;
    return scoresOverall.find((s) => s.shooterId === shooterId);
  };

  const shooter1Stages = shooter1 ? getShooterScoreByStage(shooter1.id) : [];
  const shooter2Stages = shooter2 ? getShooterScoreByStage(shooter2.id) : [];
  const shooter1Overall = shooter1 ? getShooterOverall(shooter1.id) : null;
  const shooter2Overall = shooter2 ? getShooterOverall(shooter2.id) : null;

  const toDivisionKey = (div) => {
    const map = {
      'standard': 'standard', 'production': 'production', 'open': 'open',
      'production-optics': 'productionOptics', 'optics': 'optics', 'classic': 'classic',
      'revolver': 'revolver', 'pcc-optics': 'pccOptics', 'pcc-iron': 'pccIron',
    };
    return map[div] || div;
  };

  const toCategoryKey = (cat) => {
    const map = {
      'regular': 'regular', 'senior': 'senior', 'super-senior': 'superSenior',
      'grand-senior': 'grandSenior', 'lady-senior': 'ladySenior', 'junior': 'junior',
      'super-junior': 'superJunior', 'grand-junior': 'grandJunior',
    };
    return map[cat] || cat;
  };

  const FlagName = ({ shooter }) => {
    if (!shooter) return null;
    const parts = (shooter.publicId || '').split('|');
    const displayId = parts[parts.length - 1];
    const countryCode = parts.length > 1 ? parts[0] : null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        {countryCode && (
          <img alt={countryCode} src={getFlagSrc({ countryCode, size: 16 })} style={{ display: 'block' }} />
        )}
        <Typography variant="subtitle2">{displayId} / {shooter.name}</Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: 2 }}>
      {/* Shooter Selection */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Autocomplete
          options={shooters}
          getOptionLabel={getShooterLabel}
          value={shooter1}
          onChange={(_, v) => setShooter1(v)}
          renderInput={(params) => <TextField {...params} label={t('compare.shooter1')} />}
          sx={{ flex: 1, minWidth: 250 }}
          isOptionEqualToValue={(o, v) => o?.id === v?.id}
        />
        <Autocomplete
          options={shooters}
          getOptionLabel={getShooterLabel}
          value={shooter2}
          onChange={(_, v) => setShooter2(v)}
          renderInput={(params) => <TextField {...params} label={t('compare.shooter2')} />}
          sx={{ flex: 1, minWidth: 250 }}
          isOptionEqualToValue={(o, v) => o?.id === v?.id}
        />
      </Box>

      {/* Overall Comparison */}
      {(shooter1 || shooter2) && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>{t('results.overall')}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                {shooter1 && <TableCell align="center"><FlagName shooter={shooter1} /></TableCell>}
                {shooter2 && <TableCell align="center"><FlagName shooter={shooter2} /></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'start' }}>{t('shooter.division')}</TableCell>
                {shooter1 && <TableCell align="center">{t(`divisions.${toDivisionKey(shooter1.division)}`, { defaultValue: shooter1.division })}</TableCell>}
                {shooter2 && <TableCell align="center">{t(`divisions.${toDivisionKey(shooter2.division)}`, { defaultValue: shooter2.division })}</TableCell>}
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'start' }}>{t('shooter.category')}</TableCell>
                {shooter1 && <TableCell align="center">{t(`categories.${toCategoryKey(shooter1.category)}`, { defaultValue: shooter1.category })}</TableCell>}
                {shooter2 && <TableCell align="center">{t(`categories.${toCategoryKey(shooter2.category)}`, { defaultValue: shooter2.category })}</TableCell>}
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'start' }}>{t('shooter.powerFactor')}</TableCell>
                {shooter1 && <TableCell align="center">{t(`registration.${shooter1['power-factor']}`, { defaultValue: shooter1['power-factor'] })}</TableCell>}
                {shooter2 && <TableCell align="center">{t(`registration.${shooter2['power-factor']}`, { defaultValue: shooter2['power-factor'] })}</TableCell>}
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'start' }}>{t('results.totalPoints')}</TableCell>
                {shooter1 && <TableCell align="center">{shooter1Overall ? pad(shooter1Overall.score, 4) : '-'}</TableCell>}
                {shooter2 && <TableCell align="center">{shooter2Overall ? pad(shooter2Overall.score, 4) : '-'}</TableCell>}
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'start' }}>%</TableCell>
                {shooter1 && <TableCell align="center">{shooter1Overall ? `${pad(shooter1Overall.percentage / 100, 2)}%` : '-'}</TableCell>}
                {shooter2 && <TableCell align="center">{shooter2Overall ? `${pad(shooter2Overall.percentage / 100, 2)}%` : '-'}</TableCell>}
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Stage-by-Stage Comparison */}
      {(shooter1 || shooter2) && stages.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>{t('compare.stageByStage')}</Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ textAlign: 'start' }}>{t('results.stage')}</TableCell>
                  {shooter1 && (
                    <>
                      <TableCell align="center">HF</TableCell>
                      <TableCell align="center">{t('results.stagePoints')}</TableCell>
                      <TableCell align="center">{t('results.time')}</TableCell>
                    </>
                  )}
                  {shooter1 && shooter2 && <TableCell />}
                  {shooter2 && (
                    <>
                      <TableCell align="center">HF</TableCell>
                      <TableCell align="center">{t('results.stagePoints')}</TableCell>
                      <TableCell align="center">{t('results.time')}</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {stages.map((stageItem, idx) => {
                  if (stageItem.inactive) return null;
                  const s1 = shooter1Stages[idx];
                  const s2 = shooter2Stages[idx];
                  const s1Better = s1 && s2 && s1.factor > s2.factor;
                  const s2Better = s1 && s2 && s2.factor > s1.factor;

                  return (
                    <TableRow key={idx}>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'start' }}>{t('results.stageN', { n: idx + 1 })}</TableCell>
                      {shooter1 && (
                        <>
                          <TableCell align="center" sx={{ bgcolor: s1Better ? '#e8f5e9' : s2Better ? '#ffebee' : 'inherit' }}>
                            {s1 ? pad(s1.factor, 4) : '-'}
                          </TableCell>
                          <TableCell align="center">{s1 ? pad(s1.points, 4) : '-'}</TableCell>
                          <TableCell align="center">{s1 ? pad(s1.time, 2) : '-'}</TableCell>
                        </>
                      )}
                      {shooter1 && shooter2 && <TableCell />}
                      {shooter2 && (
                        <>
                          <TableCell align="center" sx={{ bgcolor: s2Better ? '#e8f5e9' : s1Better ? '#ffebee' : 'inherit' }}>
                            {s2 ? pad(s2.factor, 4) : '-'}
                          </TableCell>
                          <TableCell align="center">{s2 ? pad(s2.points, 4) : '-'}</TableCell>
                          <TableCell align="center">{s2 ? pad(s2.time, 2) : '-'}</TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {/* Visual Bar Comparison */}
          {shooter1 && shooter2 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>{t('compare.pointsComparison')}</Typography>
              {stages.map((stageItem, idx) => {
                if (stageItem.inactive) return null;
                const s1 = shooter1Stages[idx];
                const s2 = shooter2Stages[idx];
                const s1Pts = s1?.points || 0;
                const s2Pts = s2?.points || 0;
                const stageMax = Math.max(s1Pts, s2Pts, 1);

                return (
                  <Box key={idx} sx={{ mb: 1 }}>
                    <Typography variant="caption">{t('results.stageN', { n: idx + 1 })}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            height: 20,
                            width: `${(s1Pts / stageMax) * 100}%`,
                            bgcolor: s1Pts >= s2Pts ? '#4caf50' : '#f44336',
                            borderRadius: 1,
                            minWidth: s1Pts > 0 ? 4 : 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            pr: 0.5,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: 'white', fontSize: 10 }}>
                            {s1Pts > 0 ? pad(s1Pts, 2) : ''}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            height: 20,
                            width: `${(s2Pts / stageMax) * 100}%`,
                            bgcolor: s2Pts >= s1Pts ? '#2196f3' : '#ff9800',
                            borderRadius: 1,
                            minWidth: s2Pts > 0 ? 4 : 0,
                            display: 'flex',
                            alignItems: 'center',
                            pl: 0.5,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: 'white', fontSize: 10 }}>
                            {s2Pts > 0 ? pad(s2Pts, 2) : ''}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: 0.5 }} />
                  <Typography variant="caption">{shooter1.name}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: '#2196f3', borderRadius: 0.5 }} />
                  <Typography variant="caption">{shooter2.name}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
